import React, { useMemo, useRef, useState } from "react";
import {
  FaCopy,
  FaDownload,
  FaShareAlt,
  FaExclamationTriangle,
} from "react-icons/fa";

const STORY_TEMPLATES = [
  {
    id: "instagram",
    title: "Instagram Story",
    subtitle: "استوری اینستاگرام",
    ratioClass: "w-[280px] h-[500px]",
    background: "from-[#120B2E] via-[#10182A] to-[#032B2C]",
    cta: "همین حالا برنامه خودتو بساز",
  },
  {
    id: "telegram",
    title: "Telegram",
    subtitle: "پست / استوری تلگرام",
    ratioClass: "w-[320px] h-[420px]",
    background: "from-[#082F49] via-[#10182A] to-[#312E81]",
    cta: "با کد من تخفیف بگیر",
  },
  {
    id: "whatsapp",
    title: "WhatsApp Status",
    subtitle: "وضعیت واتساپ",
    ratioClass: "w-[280px] h-[500px]",
    background: "from-[#064E3B] via-[#10182A] to-[#1E1B4B]",
    cta: "تو هم موجیمی شو",
  },
];

export default function StoryGenerator({
  referralCode,
  shareLink,
  onCopyLink,
}) {
  const storyRef = useRef(null);

  const [templateId, setTemplateId] = useState("instagram");
  const [isGenerating, setIsGenerating] = useState(false);
  const [storyError, setStoryError] = useState("");

  const selectedTemplate = useMemo(
    () =>
      STORY_TEMPLATES.find((x) => x.id === templateId) ||
      STORY_TEMPLATES[0],
    [templateId]
  );

async function createStoryImage() {
  setStoryError(
    "فعلاً ساخت خودکار تصویر غیرفعال است."
  );

  return null;
}

  async function downloadStory() {
    const dataUrl = await createStoryImage();
    if (!dataUrl) return;

    try {
      const link = document.createElement("a");
      link.download = `mogym-${templateId}-${referralCode || "story"}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error("Story download failed:", error);
      setStoryError("دانلود تصویر انجام نشد. لطفاً دوباره تلاش کن.");
    }
  }

  async function shareStory() {
    const dataUrl = await createStoryImage();
    if (!dataUrl) return;

    try {
      const blob = await (await fetch(dataUrl)).blob();

      const file = new File(
        [blob],
        `mogym-${templateId}-${referralCode || "story"}.png`,
        { type: "image/png" }
      );

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "دعوت به موجیم",
          text: "با کد دعوت من از موجیم تخفیف بگیر 💪",
          files: [file],
        });

        return;
      }

      await downloadStory();
    } catch (error) {
      console.error("Story share failed:", error);
      setStoryError(
        "اشتراک‌گذاری مستقیم در این مرورگر پشتیبانی نشد. تصویر را دانلود کن و دستی ارسال کن."
      );
    }
  }

  return (
    <section className="mt-5 rounded-[2rem] border border-white/10 bg-[#10182A]/90 p-5 shadow-xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-black text-white">
            استوری آماده دعوت
          </h2>

          <p className="mt-2 text-sm text-slate-400">
            مناسب اینستاگرام، تلگرام و وضعیت واتساپ
          </p>
        </div>
      </div>

      {storyError && (
        <div className="mb-4 rounded-2xl border border-yellow-500/20 bg-yellow-500/10 p-3 text-sm leading-7 text-yellow-100 flex gap-2">
          <FaExclamationTriangle className="mt-1 shrink-0" />
          <span>{storyError}</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 mb-5">
        {STORY_TEMPLATES.map((template) => (
          <button
            key={template.id}
            type="button"
            onClick={() => {
              setTemplateId(template.id);
              setStoryError("");
            }}
            className={`rounded-2xl border px-3 py-3 text-xs font-bold transition ${
              templateId === template.id
                ? "border-purple-500 bg-purple-500/20 text-white"
                : "border-white/10 bg-white/5 text-slate-300"
            }`}
          >
            <div>{template.title}</div>
            <div className="mt-1 text-[10px] opacity-70">
              {template.subtitle}
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-center overflow-x-auto pb-2">
        <div
          ref={storyRef}
          className={`relative ${selectedTemplate.ratioClass} shrink-0 overflow-hidden rounded-[2rem] bg-gradient-to-br ${selectedTemplate.background} p-6 text-white shadow-2xl`}
        >
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-purple-500/40 blur-3xl" />
          <div className="absolute bottom-0 -left-16 w-44 h-44 rounded-full bg-green-500/30 blur-3xl" />
          <div className="absolute top-40 left-8 w-24 h-24 rounded-full bg-blue-500/20 blur-2xl" />

          <div className="relative z-10 h-full flex flex-col items-center text-center">
            <img
              src="/logo.png"
              alt="Mogym"
              className="w-20 h-20"
              crossOrigin="anonymous"
            />

            <div className="mt-5 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-xs font-bold">
              برنامه تمرین و تغذیه هوشمند
            </div>

            <h3 className="mt-7 text-2xl font-black leading-10">
              با کد من از موجیم تخفیف بگیر 💪
            </h3>

            <div className="mt-6 w-full rounded-3xl bg-white text-slate-900 p-5">
              <div className="text-xs text-slate-500">
                کد دعوت
              </div>

              <div className="mt-1 text-3xl font-black tracking-widest">
                {referralCode || "MOGYM"}
              </div>

              <div className="mt-5 bg-white p-3 rounded-2xl inline-block">
                <div className="text-xs leading-6 break-all text-slate-600">
  {shareLink}
</div>
              </div>
            </div>

            <div className="mt-auto w-full rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 py-3 font-black">
              {selectedTemplate.cta}
            </div>

            <div className="mt-3 text-xs text-slate-300">
              mogym.ir
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <button
          type="button"
          disabled
          onClick={downloadStory}
          className="w-full px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <FaDownload />
          {isGenerating ? "در حال ساخت..." : "دانلود تصویر"}
        </button>

        <button
          type="button"
          disabled
          onClick={shareStory}
          className="w-full px-4 py-3 rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 text-white text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <FaShareAlt />
          {isGenerating ? "در حال ساخت..." : "اشتراک تصویر"}
        </button>

        <button
          type="button"
          onClick={onCopyLink}
          className="w-full px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold flex items-center justify-center gap-2 transition"
        >
          <FaCopy />
          کپی لینک
        </button>
      </div>
    </section>
  );
}